import {
    AI_PROVIDERS,
    ANTHROPIC_MODELS,
    REANALYSIS_STEPS,
} from "@mrflark/spl_sdk";
import type {
    AiProviderId,
    ReanalysisStepId,
} from "@mrflark/spl_sdk";
import {
    Button,
    Modal,
} from "@mrflark/ui";
import {
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";
import {
    Info,
} from "lucide-preact";
import {
    useState,
} from "preact/compat";
import {
    toast,
} from "react-hot-toast";

import {
    splClient,
} from "../../api/client.ts";

type ReanalysisModalProps = {
    isOpen: boolean;
    onClose: () => void;
    mediaIds: string[];
    onComplete: () => void;
};

export const ReanalysisModal = ({
    isOpen,
    onClose,
    mediaIds,
    onComplete,
}: ReanalysisModalProps) => {
    const queryClient = useQueryClient();

    const [providerId, setProviderId] = useState<AiProviderId>(
        AI_PROVIDERS[0].id,
    );
    const [modelName, setModelName] = useState<string>(
        AI_PROVIDERS[0].defaultModel,
    );
    const [selectedSteps, setSelectedSteps] = useState<Set<ReanalysisStepId>>(
        () =>
            new Set(
                REANALYSIS_STEPS
                    .filter((step) => step.defaultChecked)
                    .map((step) => step.stepId),
            ),
    );

    const selectedProvider = AI_PROVIDERS.find((provider) =>
        provider.id === providerId
    );

    const handleProviderChange = (newProviderId: AiProviderId): void => {
        setProviderId(newProviderId);
        if (newProviderId === "anthropic") {
            setModelName(ANTHROPIC_MODELS[0].id);
        } else {
            const provider = AI_PROVIDERS.find((entry) =>
                entry.id === newProviderId
            );
            if (provider) {
                setModelName(provider.defaultModel);
            }
        }
    };

    const handleStepToggle = (stepId: ReanalysisStepId): void => {
        setSelectedSteps((prev) => {
            const next = new Set(prev);
            if (next.has(stepId)) {
                next.delete(stepId);
            } else {
                next.add(stepId);
            }
            return next;
        });
    };

    const reanalyzeMutation = useMutation({
        mutationFn: () =>
            splClient.reanalyzeMedia({
                media_ids: mediaIds,
                steps: Array.from(selectedSteps),
                provider: providerId,
                model_name: modelName,
            }),
        onSuccess: (data) => {
            toast.success(
                `Queued ${data.queued_count} item${
                    data.queued_count !== 1 ? "s" : ""
                } for re-analysis`,
            );
            queryClient.invalidateQueries({
                queryKey: ["aiAnalysisItems"],
            });
            onComplete();
            onClose();
        },
        onError: (error: Error) =>
            toast.error(`Failed to queue re-analysis: ${error.message}`),
    });

    const canSubmit = mediaIds.length > 0 && selectedSteps.size > 0;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Re-Analyze Media"
            size="lg"
        >
            <div className="space-y-4">
                {/* item count */}
                <p className="text-sm text-text-secondary">
                    {mediaIds.length} item{mediaIds.length !== 1 ? "s" : ""}
                    {" "}
                    selected for re-analysis.
                </p>

                {/* provider dropdown */}
                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                        Provider
                    </label>
                    <select
                        value={providerId}
                        onChange={(event) =>
                            handleProviderChange(
                                (event.target as HTMLInputElement)
                                    .value as AiProviderId,
                            )}
                        className="input w-full"
                    >
                        {AI_PROVIDERS.map((provider) => (
                            <option key={provider.id} value={provider.id}>
                                {provider.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                        Model
                    </label>
                    {providerId === "anthropic"
                        ? (
                            <select
                                value={modelName}
                                onChange={(event) =>
                                    setModelName(
                                        (event.target as HTMLInputElement)
                                            .value,
                                    )}
                                className="input w-full"
                            >
                                {ANTHROPIC_MODELS.map((model) => (
                                    <option key={model.id} value={model.id}>
                                        {model.label}
                                    </option>
                                ))}
                            </select>
                        )
                        : (
                            <input
                                type="text"
                                value={modelName}
                                onChange={(event) =>
                                    setModelName(
                                        (event.target as HTMLInputElement)
                                            .value,
                                    )}
                                className="input w-full"
                                placeholder={selectedProvider?.defaultModel ??
                                    "model name"}
                            />
                        )}
                </div>

                {/* step checkboxes */}
                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                        Processing Steps
                    </label>
                    <div className="space-y-2">
                        {REANALYSIS_STEPS.map((step) => (
                            <label
                                key={step.stepId}
                                className="flex items-start gap-2 text-sm cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedSteps.has(step.stepId)}
                                    onChange={() =>
                                        handleStepToggle(step.stepId)}
                                    className="rounded border-border mt-0.5"
                                />
                                <div className="flex-1">
                                    <div className="text-text-primary">
                                        {step.label}
                                    </div>
                                    <div className="text-xs text-text-muted mt-0.5">
                                        {step.description}
                                    </div>
                                </div>
                            </label>
                        ))}
                    </div>
                </div>

                {/* info text */}
                <div className="flex items-start gap-2 p-3 bg-accent/10 border border-accent/20 rounded-lg">
                    <Info
                        size={16}
                        className="text-accent flex-shrink-0 mt-0.5"
                    />
                    <p className="text-xs text-text-secondary">
                        Re-analysis jobs are queued for background processing.
                        Results will appear once processing completes.
                    </p>
                </div>

                {/* actions */}
                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => reanalyzeMutation.mutate()}
                        disabled={!canSubmit || reanalyzeMutation.isPending}
                    >
                        {reanalyzeMutation.isPending
                            ? "Starting..."
                            : "Start Re-Analysis"}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
